/**
 * The leads assigned to an external salesperson, and nothing else.
 *
 * Deliberately not the leads workspace. That screen is the whole CRM — column
 * pickers, the map, the distribution board, the enrichment cards, links into
 * Ownership — and every one of those reads something an external caller is
 * refused. Reusing it would have meant a screen that mostly failed, and a long
 * list of `isStaff &&` guards inside a component that is already the largest in
 * the codebase.
 *
 * So this is its own read of one query: `where('ownerEmail', '==', me)`. That
 * filter is not a convenience — `firestore.rules` validates one document at a
 * time and never filters, so a query without it is refused outright.
 *
 * ## A row opens where it sits — 31 Aug 2026
 *
 * The list was read-only, which made it a list of names and no use for the job:
 * a rep with a company on screen needs the number to ring and somewhere to put
 * what was said. So a row expands in place, the same way the Sales CRM table now
 * does, and carries the two things it was missing — everything already on the
 * lead, and the log.
 *
 * The log is a second collection with no owner of its own, so `ownsLead` in the
 * rules resolves it through the lead. Reading is scoped by that; writing is
 * limited to `source: 'user'`, because every control that writes an `action`
 * line is on a screen this role cannot reach.
 */
import { useEffect, useState } from 'react'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { CRM_COLLECTIONS } from '@/shared/firebase/collections'
import { useAuth } from '@/features/auth/AuthProvider'
import { formatDate } from '@/shared/util/dates'
import { addNote, subscribeToActivity } from '@/features/leads/repository/activitiesRepository'
import type { Activity } from '@/types/crm'

interface LeadContact {
  name: string
  role: string
  email: string
  phone: string
}

interface Row {
  id: string
  name: string
  stage: string
  country: string
  region: string
  address: string
  website: string
  instagram: string
  contact: string
  contacts: LeadContact[]
  prospectingNotes: string
  nextTask: { text: string; date: string } | null
  updatedAt: string | null
}

const str = (v: unknown) => (typeof v === 'string' ? v : '')

export function ExternalSalesScreen() {
  const { user } = useAuth()
  const me = (user?.email ?? '').trim().toLowerCase()
  const author = user?.displayName?.trim() || me
  const [rows, setRows] = useState<Row[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)

  useEffect(() => {
    if (!me) return
    return onSnapshot(
      query(collection(db, CRM_COLLECTIONS.leads), where('ownerEmail', '==', me)),
      (snap) =>
        setRows(
          snap.docs
            .map((d) => {
              const v = d.data()
              const contacts = ((v.contacts ?? []) as Record<string, unknown>[]).map((c) => ({
                name: str(c.name),
                role: str(c.role),
                email: str(c.email),
                phone: str(c.phone),
              }))
              const task = (v.nextTask ?? null) as Record<string, unknown> | null
              return {
                id: d.id,
                name: str(v.name),
                stage: str(v.stage),
                country: str(v.country),
                region: str(v.region),
                address: str(v.address),
                website: str(v.website),
                instagram: str(v.instagram),
                contact: contacts.find((c) => c.name)?.name ?? '',
                contacts,
                prospectingNotes: str(v.prospectingNotes),
                // Only while it is still outstanding: a completed follow-up is
                // history, and this screen has the log for history.
                nextTask:
                  task && str(task.text) && !task.doneAt
                    ? { text: str(task.text), date: str(task.date) }
                    : null,
                updatedAt: typeof v.updatedAt === 'string' ? v.updatedAt : null,
              }
            })
            .sort((a, b) => a.name.localeCompare(b.name)),
        ),
      (err) => {
        setRows([])
        setError(err.message)
      },
    )
  }, [me])

  if (rows === null && !error) return <p className="empty-note">Loading your leads…</p>

  return (
    <section className="outside__card">
      <h2 className="outside__title">Your leads</h2>
      <p className="outside__lede">
        The companies assigned to you. Open one for the contact details and to log a call. Ask Skida
        to assign more, or to move one off your list.
      </p>

      {error && <p className="modal__error">{error}</p>}

      {rows && rows.length === 0 && !error && (
        <p className="empty-note">
          Nothing is assigned to you yet. Leads appear here as soon as somebody at Skida puts your
          name on one.
        </p>
      )}

      {rows && rows.length > 0 && (
        <div className="outside__table">
          <div className="outside__row outside__row--head outside__row--leads">
            <div>Company</div>
            <div>Contact</div>
            <div>Stage</div>
            <div>Country</div>
          </div>
          {rows.map((r) => {
            const open = openId === r.id
            return (
              <div className="outside__group" key={r.id}>
                {/* A second click closes it: there is no scrim over an inline
                    panel, so the row is the only way back out. */}
                <div
                  className={`outside__row outside__row--leads outside__row--open${
                    open ? ' outside__row--on' : ''
                  }`}
                  role="button"
                  tabIndex={0}
                  aria-expanded={open}
                  onClick={() => setOpenId(open ? null : r.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setOpenId(open ? null : r.id)
                    }
                  }}
                >
                  <div>{r.name}</div>
                  <div className="muted">{r.contact || '—'}</div>
                  <div className="muted">{r.stage}</div>
                  <div className="muted">
                    {r.country || '—'}
                    {r.updatedAt && (
                      <span className="outside__when"> · {formatDate(r.updatedAt.slice(0, 10))}</span>
                    )}
                  </div>
                </div>

                {open && <LeadPanel lead={r} author={author} />}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

/**
 * One lead, opened.
 *
 * Its own component so the activity subscription lives and dies with the panel.
 * Held on the screen it would be a listener per lead in the list, all but one of
 * them feeding a closed row.
 */
function LeadPanel({ lead, author }: { lead: Row; author: string }) {
  const [activity, setActivity] = useState<Activity[] | null>(null)
  const [logError, setLogError] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    setActivity(null)
    setLogError(null)
    return subscribeToActivity(
      lead.id,
      (a) => setActivity(a),
      (err) => {
        setActivity([])
        setLogError(err.message)
      },
    )
  }, [lead.id])

  const save = async () => {
    const text = draft.trim()
    if (!text) return
    setSaving(true)
    setSaveError(null)
    try {
      await addNote(lead.id, author, text)
      setDraft('')
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save the note.')
    } finally {
      setSaving(false)
    }
  }

  const where = [lead.region, lead.country].filter(Boolean).join(' · ')

  return (
    <div className="outside__panel" onClick={(e) => e.stopPropagation()}>
      <div className="outside__facts">
        <div>
          <div className="card-label">Contacts</div>
          {lead.contacts.length === 0 ? (
            <div className="muted">Nobody recorded yet.</div>
          ) : (
            lead.contacts.map((c, i) => (
              <div className="outside__person" key={`${c.email}-${i}`}>
                <div className="outside__personname">
                  {c.name || '—'}
                  {c.role && <span className="muted"> · {c.role}</span>}
                </div>
                {c.email && (
                  <div>
                    <a href={`mailto:${c.email}`}>{c.email}</a>
                  </div>
                )}
                {c.phone && (
                  <div>
                    <a href={`tel:${c.phone.replace(/\s/g, '')}`}>{c.phone}</a>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div>
          <div className="card-label">Where</div>
          <div>{where || '—'}</div>
          {lead.address && <div className="muted">{lead.address}</div>}

          <div className="card-label outside__sublabel">Links</div>
          {lead.website ? (
            <div>
              <a
                href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                target="_blank"
                rel="noreferrer"
              >
                {lead.website}
              </a>
            </div>
          ) : null}
          {lead.instagram ? (
            <div>
              <a
                href={`https://instagram.com/${lead.instagram.replace(/^@/, '')}`}
                target="_blank"
                rel="noreferrer"
              >
                @{lead.instagram.replace(/^@/, '')}
              </a>
            </div>
          ) : null}
          {!lead.website && !lead.instagram && <div className="muted">—</div>}
        </div>

        <div>
          <div className="card-label">Next follow-up</div>
          {lead.nextTask ? (
            <>
              <div>{lead.nextTask.text}</div>
              {lead.nextTask.date && (
                <div className="muted">{formatDate(lead.nextTask.date)}</div>
              )}
            </>
          ) : (
            <div className="muted">Nothing booked.</div>
          )}

          {lead.prospectingNotes && (
            <>
              <div className="card-label outside__sublabel">How this lead was found</div>
              <div className="muted">{lead.prospectingNotes}</div>
            </>
          )}
        </div>
      </div>

      {/* Enter saves. A note is one line most of the time, and reaching for a
          button after every call is the friction that stops them being written
          at all — which is how the log went quiet on the staff side too. */}
      <div className="outside__notebox">
        <input
          className="input"
          value={draft}
          placeholder="Log a call, an email, anything worth remembering…"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void save()
            }
          }}
        />
        <button
          className="btn btn--primary"
          onClick={() => void save()}
          disabled={saving || !draft.trim()}
        >
          {saving ? 'Saving…' : 'Add note'}
        </button>
      </div>
      {saveError && <p className="modal__error">{saveError}</p>}

      <div className="card-label outside__sublabel">Activity</div>
      {logError && <p className="modal__error">{logError}</p>}
      {activity === null && !logError && <div className="muted">Loading…</div>}
      {activity && activity.length === 0 && !logError && (
        <div className="muted">Nothing logged yet. Your first note goes at the top.</div>
      )}
      {activity && activity.length > 0 && (
        <ul className="outside__log">
          {activity.map((a) => (
            <li key={a.id}>
              <span className="outside__logwhen">{formatDate(a.at.slice(0, 10))}</span>
              <span className="outside__logwho">{a.author}</span>
              <span>{a.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
